import { useEffect, useMemo, useState } from 'react';
import { UserType } from '../constants/constants';
import { useFeatureContext } from '../context/FeatureContext';
import { useTranslation } from 'react-i18next';
import { getFeatureRows } from '../constants/feature';
import { useUser } from '../context/UserContext';
import { FeatureFlags, StartModule } from '../interface/featureSelection';
import { resolveStartModule } from '../utils/startModule';
import { showToast } from '../components/utils/toast';

export const useFeatureSelection = (options?: { autoSave?: boolean }) => {
  const autoSave = options?.autoSave ?? false;
  const { t } = useTranslation();
  const { user } = useUser();
  const isRestrictedUser = user?.userType === UserType.RESTRICTED;
  const {
    features,
    updateFeatures,
    startModule: savedStartModule,
    updateStartModule,
  } = useFeatureContext();
  const [featureStates, setFeatureStates] = useState<FeatureFlags>({});
  // Only onboarding holds a pick before saving; otherwise the saved start module shows through, even when it changes.
  const [unsavedStartModule, setUnsavedStartModule] = useState<StartModule>();
  const featuresRow = useMemo(
    () => getFeatureRows(t, isRestrictedUser).filter((f) => !f.hide),
    [t, isRestrictedUser],
  );

  useEffect(() => {
    if (features) {
      const visibleFeaturesOnly: FeatureFlags = {};
      featuresRow.forEach((f) => {
        visibleFeaturesOnly[f.id] = features[f.id] ?? true;
      });
      setFeatureStates(visibleFeaturesOnly);
    } else {
      const initialStates: FeatureFlags = {};
      featuresRow.forEach((feature) => {
        initialStates[feature.id] = feature.disabled ? (feature.defaultChecked ?? true) : false;
      });
      setFeatureStates(initialStates);
    }
  }, [featuresRow]);

  const startModule = resolveStartModule(featureStates, unsavedStartModule ?? savedStartModule);
  const activeFeatures = featuresRow.filter((feature) => featureStates[feature.id]);

  const persist = (states: FeatureFlags) => {
    const visibleOnly = Object.fromEntries(featuresRow.map((f) => [f.id, states[f.id]]));
    updateFeatures(visibleOnly);
  };

  const handleStartModuleChange = (nextStartModule: StartModule) => {
    if (autoSave) {
      updateStartModule(nextStartModule);
      return;
    }
    setUnsavedStartModule(nextStartModule);
  };

  const applyFeatureStates = (updatedStates: FeatureFlags) => {
    setFeatureStates(updatedStates);
    const nextStartModule = resolveStartModule(updatedStates, startModule);
    if (nextStartModule !== startModule) {
      // Stays on Dashboard when the feature is switched back on, until the user picks again.
      handleStartModuleChange(nextStartModule);
      showToast('info', t('common:features.startModuleResetToDashboard'));
    }
    if (autoSave) {
      persist(updatedStates);
    }
  };

  const handleToggle = (id: number | string) => {
    applyFeatureStates({
      ...featureStates,
      [id]: !featureStates[id],
    });
  };

  const isAllEnabled = featuresRow.filter((f) => !f.disabled).every((f) => featureStates[f.id]);

  const handleEnableAllToggle = () => {
    const updatedStates: FeatureFlags = {};
    featuresRow.forEach((feature) => {
      updatedStates[feature.id] = feature.disabled ? featureStates[feature.id] : !isAllEnabled;
    });
    applyFeatureStates(updatedStates);
  };

  const saveSelections = () => {
    persist(featureStates);
    updateStartModule(startModule);
  };

  return {
    features: featuresRow,
    featureStates,
    handleToggle,
    isAllEnabled,
    handleEnableAllToggle,
    activeFeatures,
    startModule,
    handleStartModuleChange,
    saveSelections,
  };
};
